import { useTranslation, Trans } from "react-i18next";
import { Users, Loader2, MessageCircle, Phone, Mail } from "lucide-react";
import { useEffect, useState, useCallback, useMemo } from "react";
import { Button } from "@shared/components/ui/button";
import { BookingPageLayout } from "@features/booking/layouts";
import { useBooking } from "@features/booking/state";
import { useCalendarAvailability } from "@features/booking/hooks/useCalendarAvailability";
import { useRestaurant } from "@core/tenant/TenantProvider";
import { useBookingFlow } from "@features/booking/flow";
import { MAX_PARTY_SIZE } from "@core/config/constants";
import { useAnalytics, ANALYTICS_EVENTS } from "@core/analytics";
import type { ServiceType } from "@features/booking/state/types";

const SelectDiners = () => {
  const { t, i18n } = useTranslation('booking');
  const { diners, setDiners, meitreNetworkMenuId, preSelectedMenuId } = useBooking();
  const restaurant = useRestaurant();
  const { goToNext } = useBookingFlow();
  const { trackEvent } = useAnalytics();

  const preSelectedMenu = useMemo(() => {
    if (!preSelectedMenuId) return null;
    const langMenus = restaurant.menus?.[i18n.language];
    if (!langMenus) return null;
    const menuList = Array.isArray(langMenus) ? langMenus : Object.values(langMenus);
    return menuList.find((m) => m.id === preSelectedMenuId) ?? null;
  }, [preSelectedMenuId, restaurant.menus, i18n.language]);

  useEffect(() => {
    trackEvent({ name: ANALYTICS_EVENTS.BOOKING_SELECT_DINERS });
  }, [trackEvent]);

  // Track if user has pressed "Continue" button
  const [isNavigating, setIsNavigating] = useState(false);

  // Get valid service types from restaurant
  const serviceTypes = (restaurant.serviceTypes || []) as ServiceType[];

  // Fetch calendar availability for current party size
  const {
    isLoading: isCalendarLoading,
    isSettled: isCalendarSettled,
    error: calendarError,
  } = useCalendarAvailability({
    restaurantId: restaurant.id,
    partySize: diners,
    serviceTypes,
    enabled: isNavigating && diners > 0,
    meitreNetworkMenuId,
    preSelectedMenuId,
  });

  // Navigate when calendar data is ready
  useEffect(() => {
    if (isNavigating && isCalendarSettled && !calendarError) {
      goToNext();
    }
  }, [isNavigating, isCalendarSettled, calendarError, goToNext]);

  // Handle navigation request
  const handleNext = useCallback(() => {
    if (diners > 0) {
      setIsNavigating(true);
    }
  }, [diners]);

  // Reset navigation state on error
  useEffect(() => {
    if (calendarError) {
      setIsNavigating(false);
    }
  }, [calendarError]);

  // Compute effective min/max — menu-specific limits take priority when pre-selected
  const minPartySize = preSelectedMenu?.minPartySizeAllowed || restaurant.minPartySizeAllowed || 1;

  const effectiveMax = useMemo(() => {
    if (preSelectedMenu?.maxPartySizeAllowed) return preSelectedMenu.maxPartySizeAllowed;
    const allowed = restaurant.allowedPartySizes;
    if (allowed && allowed.length > 0) return Math.max(...allowed);
    if (restaurant.maxPartySizeAllowed) return restaurant.maxPartySizeAllowed;
    return MAX_PARTY_SIZE;
  }, [preSelectedMenu, restaurant.allowedPartySizes, restaurant.maxPartySizeAllowed]);

  const allowLargeRequest = !!restaurant.allowLargePartySizeRequest;
  const spinnerMax = allowLargeRequest ? 49 : effectiveMax;
  const isPartyTooBig = diners > effectiveMax && allowLargeRequest;

  // Contact info for large party requests
  const hasWhatsApp = !!restaurant.whatsAppPhoneNumber;
  const hasPhone = !!restaurant.phone && !hasWhatsApp;
  const hasEmail = !!restaurant.contactEmail && restaurant.contactEmail !== 'contact@meitre.com';

  // Dynamic quick buttons: minPartySize to minPartySize+3, filtered by effectiveMax
  const popularSizes = useMemo(() => {
    const sizes: number[] = [];
    for (let i = minPartySize; i <= minPartySize + 3 && i <= effectiveMax; i++) {
      sizes.push(i);
    }
    return sizes;
  }, [minPartySize, effectiveMax]);

  const getPartyDescription = (size: number) => {
    if (size === 1) return t('PARTYSIZE_JUST_ME');
    if (size === 2) return t('PARTYSIZE_WITH_COMPANY');
    if (size <= 4) return t('PARTYSIZE_SMALL_GROUP');
    if (size <= 8) return t('PARTYSIZE_MEDIUM_GROUP');
    return t('PARTYSIZE_LARGE_PARTY');
  };

  const isCustomActive = diners > 0 && !popularSizes.includes(diners);

  const showCustomSpinner =
    effectiveMax > popularSizes[popularSizes.length - 1] || allowLargeRequest;

  useEffect(() => {
    if (diners < minPartySize || diners > effectiveMax) {
      setDiners(Math.min(Math.max(diners, minPartySize), effectiveMax));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <BookingPageLayout
      title={t('AMOUNT_OF')}
      subtitle={t('START_STRING_4')}
      contentClassName="pt-12 pb-12 flex flex-col items-center"
      subtitleClassName="text-foreground/50 text-sm mb-10"
    >
      <div className="w-full px-6 mb-8">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {popularSizes.map((size) => (
            <button
              key={size}
              data-testid={`diner-btn-${size}`}
              className={`relative h-20 rounded-xl flex flex-col items-center justify-center transition-all duration-200 border ${
                diners === size
                  ? "bg-brand text-brand-foreground border-brand-dark shadow-md"
                  : "bg-card border-border text-foreground/70 hover:border-brand-light"
              }`}
              onClick={() => setDiners(size)}
            >
              <span className="text-2xl font-medium">{size}</span>
              <span className="text-xs mt-1 opacity-80">
                {size === 1 ? t('FILTER_STRING_PERSON') : t('FILTER_STRING_PEOPLE2')}
              </span>
            </button>
          ))}
        </div>
      </div>

      {showCustomSpinner && (
      <div className="w-full px-6">
        <div className="p-4 bg-card rounded-xl border border-border shadow-sm">
          <p className="text-sm text-foreground/50 mb-3">{t('PARTYSIZE_CUSTOM')}</p>
          <div className="flex items-center">
            <button
              data-testid="diners-minus"
              aria-label={t('A11Y_DECREASE_PARTY_SIZE')}
              className="w-12 h-12 rounded-l-lg bg-foreground/5 flex items-center justify-center text-foreground/70 border border-border"
              onClick={() => setDiners(Math.max(minPartySize, diners - 1))}
              disabled={diners <= minPartySize}
            >
              <span className="text-xl font-medium">-</span>
            </button>

            <div
              className={`flex-1 h-12 border-t border-b flex items-center justify-center transition-colors duration-200 ${
                isCustomActive
                  ? "bg-brand text-brand-foreground border-brand-dark"
                  : "border-border"
              }`}
            >
              <div className="flex flex-col items-center">
                <span className="text-xl font-medium">{diners}</span>
                <span
                  className={`text-xs ${
                    isCustomActive ? "opacity-80" : "text-foreground/50"
                  }`}
                >
                  {getPartyDescription(diners)}
                </span>
              </div>
            </div>

            <button
              data-testid="diners-plus"
              aria-label={t('A11Y_INCREASE_PARTY_SIZE')}
              className="w-12 h-12 rounded-r-lg bg-foreground/5 flex items-center justify-center text-foreground/70 border border-border"
              onClick={() => setDiners(Math.min(spinnerMax, diners + 1))}
              disabled={diners >= spinnerMax}
            >
              <span className="text-xl font-medium">+</span>
            </button>
          </div>
        </div>
        {!isPartyTooBig && diners === effectiveMax && (
          <p className="text-xs text-foreground/40 mt-2 text-center">
            {t('BOOK_FOR_OVER_2')}
          </p>
        )}
      </div>
      )}

      <div className="w-full px-6 mt-12">
        {isPartyTooBig ? (
          <div className="text-center space-y-4">
            <p className="text-sm text-foreground/70">
              <Trans
                i18nKey="MODAL_LARGE_PARTYSIZE_DIALOG_TITLE"
                t={t}
                values={{ maxSize: effectiveMax }}
                components={{ strong: <strong className="font-semibold" /> }}
              />
            </p>
            <div className="flex flex-col gap-3">
              {hasWhatsApp && (
                <Button asChild className="bg-[#25D366] text-white font-medium py-5 px-6 rounded-xl w-full hover:bg-[#1da851] shadow-md flex items-center justify-center gap-2">
                  <a
                    href={`https://wa.me/${restaurant.whatsAppPhoneNumber}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <MessageCircle size={18} />
                    {t('GO_TO_WHATSAPP')}
                  </a>
                </Button>
              )}
              {hasPhone && (
                <Button asChild className="bg-brand text-brand-foreground font-medium py-5 px-6 rounded-xl w-full hover:bg-brand-dark shadow-md flex items-center justify-center gap-2">
                  <a href={`tel:${restaurant.phone}`}>
                    <Phone size={18} />
                    {t('CALL')}
                  </a>
                </Button>
              )}
              {hasEmail && (
                <Button asChild className="bg-card text-foreground/70 font-medium py-5 px-6 rounded-xl w-full border border-border hover:bg-foreground/5 shadow-sm flex items-center justify-center gap-2">
                  <a href={`mailto:${restaurant.contactEmail}`}>
                    <Mail size={18} />
                    {t('REQUEST_BY_EMAIL')}
                  </a>
                </Button>
              )}
            </div>
          </div>
        ) : (
          <>
            <Button
              data-testid="continue-btn"
              className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md flex items-center justify-center gap-2"
              onClick={handleNext}
              disabled={diners < minPartySize || isNavigating}
            >
              {isNavigating && isCalendarLoading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  {t('PARTYSIZE_LOADING')}
                </>
              ) : (
                <>
                  <Users size={18} />
                  {t('PARTYSIZE_CONTINUE', { count: diners })}
                </>
              )}
            </Button>

            {calendarError && (
              <p className="text-red-500 text-sm mt-2 text-center">
                {t('CALENDAR_LOAD_ERROR')}
              </p>
            )}
          </>
        )}
      </div>
    </BookingPageLayout>
  );
};

export default SelectDiners;
